/**
 * @file        src/pages/seo/SeoSettings.jsx
 * @description SEO Management → Global SEO: the brand-level defaults every page
 *              falls back to (site name, title template, default description and
 *              social image, canonical host, locale), search-console
 *              verification, analytics IDs, Organization schema — and the
 *              "Do not index this site" kill-switch.
 *
 *              THE KILL-SWITCH SAVES ON ITS OWN, after a confirm. It is the one
 *              setting that can drop a live storefront out of Google, so it is
 *              not a checkbox that rides along with an unrelated Save: what the
 *              danger zone shows is always what the server holds.
 *
 *              The preview card on the right is the HOME page resolved with the
 *              draft settings (POST /api/seo/preview), so a template or default
 *              change is seen in the storefront's own words before it is saved.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import ImageUpload from '../../components/ImageUpload';
import { getSeoSettings, previewSeo, saveSeoSettings } from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import {
  BrandBar, Counter, DESCRIPTION_LIMIT, Field, GooglePreview, NoindexBanner, PickBrand, SectionTitle,
  SocialPreview, errFields, errMessage, useDebounced, useSeoBrand,
} from './seoShared';

const TEXT_KEYS = [
  'site_name', 'title_template', 'default_meta_description', 'default_og_image_url', 'twitter_handle',
  'facebook_app_id', 'canonical_host', 'locale', 'google_site_verification', 'bing_site_verification',
  'ga_measurement_id', 'gtm_container_id', 'facebook_pixel_id', 'organization_schema',
];

function formFrom(row) {
  const f = {};
  for (const k of TEXT_KEYS) f[k] = row?.[k] ?? '';
  if (!f.locale) f.locale = 'en_IN';
  f.sitemap_enabled = row ? Number(row.sitemap_enabled) !== 0 : true;
  return f;
}

function jsonProblem(text) {
  if (!text || !text.trim()) return null;
  try {
    const v = JSON.parse(text);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Give a single JSON object — it is merged into the generated Organization block.';
    return null;
  } catch (e) {
    return `Not valid JSON — ${e.message}`;
  }
}

export default function SeoSettings() {
  const qc = useQueryClient();
  const [brand, setBrand] = useSeoBrand();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.seo.settings(brand),
    queryFn: () => getSeoSettings(brand),
    enabled: Boolean(brand),
  });
  const saved = data?.data;
  useEffect(() => { if (saved) setForm(formFrom(saved)); }, [saved]);

  const draft = useDebounced(form, 400);
  const previewBody = useMemo(
    () => (draft && brand ? { brand, pageKey: 'home', settings: draft } : null),
    [draft, brand],
  );
  const preview = useQuery({
    queryKey: qk.seo.preview(previewBody),
    queryFn: () => previewSeo(previewBody),
    enabled: Boolean(previewBody),
    placeholderData: keepPreviousData,
  });
  const resolved = preview.data?.data || null;

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.seo.root });
  const saveMut = useMutation({
    mutationFn: saveSeoSettings,
    onSuccess: () => { toast.ok('Saved', 'Global SEO updated.'); setErrors({}); invalidate(); },
    onError: (err) => { setErrors(errFields(err)); toast.err('Could not save', errMessage(err, 'Validation failed')); },
  });
  const killMut = useMutation({
    mutationFn: (on) => saveSeoSettings({ brand, noindex_site: on }),
    onSuccess: (_r, on) => {
      toast.ok(on ? 'Site set to noindex' : 'Site indexable again', on
        ? 'Every page of this storefront is now served noindex.'
        : 'Pages are indexable again on the canonical host.');
      invalidate();
    },
    onError: (err) => toast.err('Could not change', errMessage(err)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const orgProblem = form ? jsonProblem(form.organization_schema) : null;
  const killOn = Number(saved?.noindex_site) === 1;
  const reason = killOn ? 'kill-switch' : (!saved?.canonical_host ? 'no-canonical-host' : null);

  function onSubmit(e) {
    e.preventDefault();
    if (orgProblem) { setErrors({ organization_schema: orgProblem }); return; }
    saveMut.mutate({ brand, ...form });
  }

  async function toggleKill() {
    const turningOn = !killOn;
    const ok = await confirm(turningOn ? {
      title: 'Stop search engines indexing this storefront?',
      message: 'Every page will be served "noindex, nofollow" and robots.txt will answer "Disallow: /". On a LIVE site, Google drops its pages within days and recovering rankings can take weeks. Use this for staging and pre-launch only.',
      confirmLabel: 'Turn on noindex',
      danger: true,
    } : {
      title: 'Let search engines index this storefront?',
      message: 'Pages become indexable on the canonical host. Do this only on the production storefront.',
      confirmLabel: 'Allow indexing',
      danger: false,
    });
    if (ok) killMut.mutate(turningOn);
  }

  return (
    <>
      <PageHead title="Global SEO" subtitle="SEO Management · brand defaults every page falls back to" />
      <BrandBar brand={brand} onChange={setBrand} />
      {!brand ? <PickBrand /> : isError ? (
        <article className="panel glass"><div className="empty"><p className="field-error">Could not load settings.</p></div></article>
      ) : isLoading || !form ? (
        <article className="panel glass"><div className="empty">Loading…</div></article>
      ) : (
        <>
          <NoindexBanner reason={reason} brand={brand} />

          <div className={`seo-danger${killOn ? ' on' : ''}`} style={{ marginBottom: 16 }}>
            <h4>Do not index this site {killOn ? '— ON' : '— off'}</h4>
            <p className="muted" style={{ margin: '0 0 12px' }}>
              The staging kill-switch. When ON, every page of this storefront is served <b>noindex</b> on every
              host and robots.txt answers <code>Disallow: /</code>, whatever the pages themselves say. It can only
              ever remove visibility. Leave it OFF on the production storefront.
            </p>
            <button type="button" className={`btn ${killOn ? 'primary' : 'danger'} sm`} onClick={toggleKill} disabled={killMut.isPending}>
              {killMut.isPending ? 'Saving…' : killOn ? 'Allow indexing' : 'Turn on noindex'}
            </button>
          </div>

          <div className="seo-split">
            <article className="panel glass">
              <form className="form-grid" onSubmit={onSubmit} style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <SectionTitle>Site</SectionTitle>
                <Field label="Canonical host" full err={errors.canonical_host}
                  hint="The production address, e.g. https://www.example.com. Every canonical, sitemap and social URL is built from it — and until it is set, the storefront serves every page noindex on every host. Any other host (staging, an IP) is always served noindex.">
                  <input value={form.canonical_host} onChange={set('canonical_host')} placeholder="https://www.example.com" maxLength={200} />
                </Field>
                <Field label="Site name" err={errors.site_name} hint="Defaults to the brand name.">
                  <input value={form.site_name} onChange={set('site_name')} maxLength={120} />
                </Field>
                <Field label="Title template" err={errors.title_template} hint='Must contain {page}, e.g. "{page} | Neon Holidays".'>
                  <input value={form.title_template} onChange={set('title_template')} maxLength={160} placeholder="{page} | Site name" />
                </Field>
                <Field label="Default meta description" full err={errors.default_meta_description}>
                  <textarea rows={3} value={form.default_meta_description} onChange={set('default_meta_description')} maxLength={500} />
                  <Counter value={form.default_meta_description} limit={DESCRIPTION_LIMIT} />
                </Field>
                <Field label="Default social image (1200×630)" full err={errors.default_og_image_url}>
                  <ImageUpload value={form.default_og_image_url} onChange={set('default_og_image_url')} />
                </Field>
                <Field label="Locale" err={errors.locale} hint="Language_COUNTRY, e.g. en_IN.">
                  <input value={form.locale} onChange={set('locale')} maxLength={10} />
                </Field>
                <Field label="Sitemap">
                  <label className="check">
                    <input type="checkbox" checked={form.sitemap_enabled} onChange={set('sitemap_enabled')} />
                    <span>Publish sitemap.xml</span>
                  </label>
                </Field>

                <SectionTitle>Social</SectionTitle>
                <Field label="Twitter / X handle" err={errors.twitter_handle}>
                  <input value={form.twitter_handle} onChange={set('twitter_handle')} placeholder="@handle" maxLength={16} />
                </Field>
                <Field label="Facebook App ID" err={errors.facebook_app_id}>
                  <input value={form.facebook_app_id} onChange={set('facebook_app_id')} inputMode="numeric" maxLength={20} />
                </Field>

                <SectionTitle>Verification &amp; analytics</SectionTitle>
                <Field label="Google site verification" err={errors.google_site_verification} hint='Only the content="…" value.'>
                  <input value={form.google_site_verification} onChange={set('google_site_verification')} maxLength={120} />
                </Field>
                <Field label="Bing site verification" err={errors.bing_site_verification} hint='Only the content="…" value.'>
                  <input value={form.bing_site_verification} onChange={set('bing_site_verification')} maxLength={120} />
                </Field>
                <Field label="GA4 measurement ID" err={errors.ga_measurement_id}>
                  <input value={form.ga_measurement_id} onChange={set('ga_measurement_id')} placeholder="G-XXXXXXX" maxLength={30} />
                </Field>
                <Field label="GTM container ID" err={errors.gtm_container_id}>
                  <input value={form.gtm_container_id} onChange={set('gtm_container_id')} placeholder="GTM-XXXXXXX" maxLength={30} />
                </Field>
                <Field label="Meta (Facebook) Pixel ID" err={errors.facebook_pixel_id}>
                  <input value={form.facebook_pixel_id} onChange={set('facebook_pixel_id')} inputMode="numeric" maxLength={30} />
                </Field>

                <SectionTitle>Organization schema</SectionTitle>
                <Field label="Extra Organization JSON-LD" full err={errors.organization_schema || orgProblem}
                  hint="Optional. One JSON object merged over the generated TravelAgency block (name, logo, contact and social links are filled in from Site Setting already).">
                  <textarea className="seo-code" rows={7} value={form.organization_schema} onChange={set('organization_schema')}
                    spellCheck={false} placeholder={'{\n  "foundingDate": "2015"\n}'} />
                </Field>

                <div className="form-actions">
                  <button className="btn primary" type="submit" disabled={saveMut.isPending}>
                    <Icon name="check" /> {saveMut.isPending ? 'Saving…' : 'Save Global SEO'}
                  </button>
                </div>
              </form>
            </article>

            <aside className="seo-side">
              <article className="panel glass">
                <div className="seo-preview-label">Home page in Google</div>
                <GooglePreview resolved={resolved} loading={preview.isFetching} />
              </article>
              <article className="panel glass">
                <div className="seo-preview-label">Home page social card</div>
                <SocialPreview resolved={resolved} />
              </article>
            </aside>
          </div>
        </>
      )}
    </>
  );
}
